import { HtmlString, stripHtml } from '@breezy/shared'

export type MergeSummaryMode = 'replace' | 'append' | 'set-if-empty'

type HtmlStringLike = HtmlString | string | undefined

/**
 * Merge a visit note into a job summary HTML.
 *
 * @param existingSummaryHtml - The current job summary (HTML)
 * @param visitNoteHtml - The visit note to merge (already HTML from WYSIWYG editor)
 * @param mode - How to merge: 'replace' overwrites, 'append' adds to end, 'set-if-empty' only sets if empty
 * @returns The merged summary HTML
 */
export const mergeSummaryWithVisitNote = ({
  existingSummaryHtml,
  visitNoteHtml,
  mode,
}: {
  existingSummaryHtml?: HtmlStringLike
  visitNoteHtml?: HtmlStringLike
  mode: MergeSummaryMode
}): HtmlStringLike => {
  if (!visitNoteHtml) return existingSummaryHtml

  if (!existingSummaryHtml || mode === 'replace') {
    return visitNoteHtml
  }

  if (mode === 'set-if-empty') {
    return existingSummaryHtml
  }

  // Append mode - check if visit note already exists to prevent double-append
  const existingPlain = stripHtml(existingSummaryHtml)
  const visitPlain = stripHtml(visitNoteHtml)

  if (existingPlain.includes(visitPlain)) {
    return existingSummaryHtml
  }

  const visitNoteHtmlWithoutLeadingParagraph = (visitNoteHtml as string | undefined)?.replace(/^<p>(.*)<\/p>$/s, '$1')
  const existingSummaryHtmlWithoutLeadingParagraph = (existingSummaryHtml as string | undefined)?.replace(
    /^<p>(.*)<\/p>$/s,
    '$1',
  )

  return `<p>${existingSummaryHtmlWithoutLeadingParagraph}<br/>${visitNoteHtmlWithoutLeadingParagraph}</p>`
}

/**
 * Check if visit note content is already in the summary.
 * Returns true when the visit note is whitespace-only or when its text already appears in the summary.
 * Returns false when either input is missing or when the visit note content is not yet present.
 */
export const isVisitNoteInSummary = (summaryHtml?: HtmlStringLike, visitNoteHtml?: HtmlStringLike): boolean => {
  if (!summaryHtml || !visitNoteHtml) return false

  const visitPlain = stripHtml(visitNoteHtml as string).trim()
  if (!visitPlain) return true // Empty/whitespace-only visit note = nothing to merge

  const summaryPlain = stripHtml(summaryHtml as string)
  return summaryPlain.includes(visitPlain)
}
